import { createMatchSelector, push } from 'connected-react-router';

import { dispatch, getStore, getState } from 'pluginServices/store';
import StateSubscriber from 'pluginServices/event/StateSubscriber';
import clearSelectionAction from 'pluginServices/editorState/actionCreators/clearSelection';
import addSelectionAction from 'pluginServices/editorState/actionCreators/addSelection';
import {showDeleteConfirmationModal, showCommentDeleteConfirmation, showTemplateModal} from 'pluginServices/editorState/actionCreators/userResponse';

import UndoRedoService from 'services/UndoRedoService';
import { CONFIRM_ACTION_DELETE, CONFIRM_ACTION_DELETE_SECTION, CONFIRM_COMMENT_DELETE } from '../constants';

class EditorServices {
  constructor({ id, context }) {
    this.id = id;
    this.context = context;
    this._subscriptions = [];
    this._selections = [];
    this._initialize();
  }

  _initialize() {
    this.stateSubscriber = new StateSubscriber(getStore());
    this.stateSubscriber.subscribe(this._handleSelectionsChange.bind(this), ['editorState', 'selections']);
  }

  _handleSelectionsChange(selections) {
    this._selections = selections;
    this._subscriptions.forEach(this._notifySubscription.bind(this));
  }

  _notifySubscription(subscription) {
    const scopedSelections = [];

    this._selections.forEach((selection) => {
      const selectionPath = selection.path.join('.');
      const subscriptionPath = subscription.path.join('.');

      if (selectionPath.indexOf(subscriptionPath) === 0) {
        scopedSelections.push(selection);
      }
    });

    subscription.listener(scopedSelections);
  }

  _createUnsubscribe(subscription) {
    return () => {
      const subscriptionIndex = this._subscriptions.indexOf(subscription);
      this._subscriptions.splice(subscriptionIndex, 1);
    };
  }

  clearSelection(scope) {
    dispatch(clearSelectionAction(scope));
  }

  addSelection(selection) {
    dispatch(addSelectionAction(selection));
  }

  subscribeToSelection(selection, listener, isAsyncListener) {
    const newSubscription = {
      path: selection.path,
      data: selection.data,
      listener,
    };

    this._subscriptions.push(newSubscription);
    if (!isAsyncListener) {
      this._notifySubscription(newSubscription);
      return this._createUnsubscribe(newSubscription);
    }

    // ES2018 style. Client will create a subscription listener, so notify it.
    return {
      unsubscribeFn: this._createUnsubscribe(newSubscription),
      initialNotifyAfterReturn: () => this._notifySubscription(newSubscription),
    };
  }

  confirmAction(actionIdentifer, comment=null) {
    if (actionIdentifer === CONFIRM_ACTION_DELETE || actionIdentifer === CONFIRM_ACTION_DELETE_SECTION) {
      dispatch(showDeleteConfirmationModal(true));
    } else if(actionIdentifer === CONFIRM_COMMENT_DELETE){
      dispatch(showCommentDeleteConfirmation(true, comment));
    }
  }

  recordUndoRedoAction(action, config) {
    UndoRedoService.recordAction(action, config, this.id);
  }

  createFromTemplate (templateDetail) {
    dispatch(showTemplateModal(true, templateDetail));
  }

  open(otherActivityId) {
    // createMatchSelector expects `router`, not `route`, and expects a non-immutable state
    const nonImmutableState = { router: { location: getState().getIn(['route', 'location']).toJS() } };

    const isOnLessonContentPage = createMatchSelector('/lesson/:lessonId/content');
    const isInLessonBuilder = createMatchSelector('/lesson/:lessonId');
    const isInCourseBuilder = createMatchSelector('/course/:courseId');

    const lessonContentMatch = isOnLessonContentPage(nonImmutableState);

    if (lessonContentMatch !== null) {
      dispatch(push(`/lesson/${lessonContentMatch.params.lessonId}/pathway/${this.id}/screen/${otherActivityId}`));
    } else {
      const lessonBuilderMatch = isInLessonBuilder(nonImmutableState);
      const courseBuilderMatch = isInCourseBuilder(nonImmutableState);

      if (lessonBuilderMatch !== null) {
        dispatch(push(`/lesson/${lessonBuilderMatch.params.lessonId}/edit/${otherActivityId}`));
      } else if (courseBuilderMatch !== null) {
        dispatch(push(`/course/${courseBuilderMatch.params.courseId}/edit/${otherActivityId}`));
      }
    }
  }

  static build(args) {
    return new EditorServices(args);
  }
}

export default EditorServices;
